import { DatePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChildren,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CustomTabInfo } from '@teorema/patron';
import { TicketTask } from 'src/app/models';
import { TcTicketService } from 'src/app/shared/services';
import { SprintStateService } from '../services/sprint-state.service';
import { Subscription } from 'rxjs';

export interface SprintTicketTask extends TicketTask {
  badge?: boolean;
  ticketTasks?: SprintTicketTask[];
  title?: string;
}

export interface SprintUserBucket {
  estimatedEffort: number;
  finishedPoints: number;
  ticketTasks: SprintTicketTask[];
  hasUrgent?: boolean;
  hasFailedTests?: boolean;
  hasBug?: boolean;
  hasPendingRelease?: boolean;
}

export type SprintBucket = Record<string, SprintUserBucket>;

export interface SprintOpenedChange {
  user?: string;
  opened?: boolean;
  state: Record<string, boolean>;
}

@Component({
  selector: 'app-sprint-column',
  templateUrl: './sprint-column.component.html',
  styleUrls: ['./sprint-column.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SprintColumnComponent implements OnChanges, AfterViewInit, OnInit, OnDestroy {
  @Input() color?: string;
  @Input() title?: string | undefined | null;
  @Input() sprintObj: SprintBucket | null = null;
  @Input() sprintOpenedUser: Record<string, boolean> | null = null;
  @Input() minimizedSprints: Record<string, string> | null = null;
  @Input() readOnly: boolean = false;
  @Input() useSprintObjLength: boolean = false;
  //SprintBeta
  @Input() isBeta: boolean = false;

  @Output() openTicketModalEmitter = new EventEmitter<SprintTicketTask>();
  @Output() titleClicked = new EventEmitter();
  @Output() dropEmitter = new EventEmitter();
  @Output() dragStartEmitter = new EventEmitter();
  @Output() dragEndEmitter = new EventEmitter();
  @Output() openedChange = new EventEmitter<SprintOpenedChange>();

  hasUrgent: boolean = false;
  hasFailedTests: boolean = false;
  hasBug: boolean = false;
  hasPendingRelease: boolean = false;

  totalCompletedPoints = 0;
  totalPoints = 0;
  totalSprintObjLength = 0;
  tabs: CustomTabInfo[] = [];
  public ticketTasksMap: Record<string, SprintTicketTask[]> = {};
  // private openedState: Record<string, boolean> = {}; // Removed local state
  private viewInitialized = false;
  private pendingTabsUpdate = false;
  private _userNames: string[] = [];

  @ViewChildren('userHeader') userHeaders?: QueryList<TemplateRef<any>>;
  @ViewChildren('userContent') userContents?: QueryList<TemplateRef<any>>;

  private sprintStateService = inject(SprintStateService);
  private subscription = new Subscription();
  private activeUser: string | null = null;

  constructor(
    public tcTicketService: TcTicketService,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sprintObj']) {
      this._userNames = this.sprintObj ? this.getObjectKeys(this.sprintObj) : [];
      this.calculateTotalPoints();
      this.buildTicketTasksMap();
      this.scheduleTabsUpdate();
    }
  }

  ngOnInit(): void {
    this.subscription.add(
      this.sprintStateService.select('activeDeveloper').subscribe(user => {
        this.activeUser = user;
        this.updateActiveIndex();
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    if (this.pendingTabsUpdate) {
      this.queueTabsUpdate();
      this.pendingTabsUpdate = false;
    } else {
      this.queueTabsUpdate();
    }

    this.userHeaders?.changes.subscribe(() => this.queueTabsUpdate());
    this.userContents?.changes.subscribe(() => this.queueTabsUpdate());
  }

  calculateTotalPoints() {
    if (!this.sprintObj) {
      this.resetTotals();
      return;
    }

    this.resetTotals();

    const values = Object.values(this.sprintObj);

    if (this.useSprintObjLength) {
      this.totalSprintObjLength = values.reduce((acc: number, obj: any) => {
        return acc + (obj.ticketTasks?.length || 0);
      }, 0);
    } else {
      values.forEach((obj: any) => {
        this.totalPoints += obj.estimatedEffort || 0;
        this.totalCompletedPoints += obj.finishedPoints || 0;
        this.hasUrgent = this.hasUrgent || !!obj.hasUrgent;
        this.hasFailedTests = this.hasFailedTests || !!obj.hasFailedTests;
        this.hasBug = this.hasBug || !!obj.hasBug;
        this.hasPendingRelease = this.hasPendingRelease || !!obj.hasPendingRelease;
      });
    }
  }

  private resetTotals() {
    this.totalCompletedPoints = 0;
    this.totalPoints = 0;
    this.totalSprintObjLength = 0;
    this.hasUrgent = false;
    this.hasFailedTests = false;
    this.hasBug = false;
    this.hasPendingRelease = false;
  }

  private buildTicketTasksMap() {
    this.ticketTasksMap = {};

    this.userNames.forEach(user => {
      const bucket = this.sprintObj?.[user];
      const tasks = bucket?.ticketTasks ?? [];
      this.ticketTasksMap[user] = this.isBeta ? this.removerTicketsDuplicados(tasks) : tasks;
    });
  }

  private scheduleTabsUpdate() {
    if (!this.viewInitialized) {
      this.pendingTabsUpdate = true;
      return;
    }

    this.queueTabsUpdate();
  }

  private queueTabsUpdate() {
    Promise.resolve().then(() => this.updateTabs());
  }

  private updateTabs() {
    if (!this.viewInitialized) return;

    this.tabs = [];

    for (let i = 0; i < this.userNames.length; i++) {
      const header = this.userHeaders?.get(i);
      const content = this.userContents?.get(i);

      if (header && content) {
        this.tabs.push({ header, content });
      }
    }

    this.updateActiveIndex();
    this.cdr.markForCheck();
  }

  getObjectKeys(object) {
    return Object.keys(object).sort();
  }

  openTicketModal(event: MouseEvent, ticketTask: SprintTicketTask) {
    event.preventDefault();
    event.stopPropagation();
    this.openTicketModalEmitter.emit(ticketTask);
  }

  isMinimized() {
    return this.minimizedSprints?.hasOwnProperty(this.formattedTitle!) ?? false;
  }

  get userNames() {
    return this._userNames;
  }

  tabOpened(event) {
    const index = typeof event === 'number' ? event : event?.index;
    this.toggleTabOpened(index, true);
  }

  tabClosed(event) {
    const index = typeof event === 'number' ? event : event?.index;
    this.toggleTabOpened(index, false);
  }

  toggleTabOpened(index: number, value: boolean) {
    let user = this.getUserNameFromIndex(index);

    if (!user) {
      return;
    }

    // Update global state
    if (value) {
      this.sprintStateService.update({ activeDeveloper: user });
    } else if (this.activeUser === user) {
      this.sprintStateService.update({ activeDeveloper: null });
    }
  }

  getUserNameFromIndex(index?: number): string | undefined {
    if (index != undefined && index != null && index > -1 && index < this.userNames.length) {
      return this.userNames[index];
    }
    return undefined;
  }

  updateActiveIndex() {
    this.activeIndex = this.userNames.findIndex(user => this.activeUser === user);
  }

  activeIndex: number | number[] = -1;

  get formattedTitle() {
    return this.title && this.title.split('-').length === 3
      ? this.datePipe.transform(this.title, 'dd/MM/yyyy')
      : this.title;
  }

  removerTicketsDuplicados(lista: SprintTicketTask[]): SprintTicketTask[] {
    if (this.title !== 'Atrasados') {
      const idsVistos = new Set<string>();
      return lista.filter(item => {
        const ticketId = item.ticket?.id;
        if (!ticketId || idsVistos.has(ticketId)) {
          return false;
        }
        idsVistos.add(ticketId);
        return true;
      });
    } else {
      lista?.forEach(element => {
        const idsVistos = new Set<string>();
        element.ticketTasks = element.ticketTasks?.filter(item => {
          item.title = element.title;
          const ticketId = item.ticket?.id;
          if (!ticketId || idsVistos.has(ticketId)) {
            return false;
          }
          idsVistos.add(ticketId);
          return true;
        });
      });
      return lista;
    }
  }

  trackByUser(index: number, user: string): string {
    return user;
  }

  trackByTask(index: number, task: SprintTicketTask): string | number {
    return task?.ticket?.id || task?.task?.id || index;
  }

  private emitOpenedState(user?: string, opened?: boolean, state?: Record<string, boolean>) {
    this.openedChange.emit({
      user,
      opened,
      state: state || {},
    });
  }
}
